import { useEffect, useRef, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Spinner } from '../Spinner';
import { ErrorMessage } from '../ErrorMessage';
import { GlanceCard } from '../shell/GlanceCard';
import { MetricRow } from '../shell/MetricRow';
import { KingdomBrowseControls } from './KingdomBrowseControls';
import { RANK_ORDER } from './KingdomRosterTable';
import { formatNumber, formatEnumLabel, formatNetworthPerAcre } from '../../utils/format';
import { isProtected } from '../../utils/protection';
import { memberToProvinceSnapshot } from '../../utils/provinceSnapshot';
import { COLORS } from '../../constants/theme';
import type { KingdomViewerShellProps } from './types';
import type { Kingdom, KingdomMember } from '../../types/kingdom';

type SortKey = 'province' | 'race' | 'land' | 'networth' | 'nwpa' | 'rank';

const SORT_OPTIONS: Array<{ key: SortKey; label: string }> = [
  { key: 'province', label: 'Province' },
  { key: 'race', label: 'Race' },
  { key: 'land', label: 'Land' },
  { key: 'networth', label: 'Net Worth' },
  { key: 'nwpa', label: 'NW/Acre' },
  { key: 'rank', label: 'Rank' },
];

function sortAccessor(m: KingdomMember, key: SortKey): string | number {
  switch (key) {
    case 'province': return m.provinceName;
    case 'race': return m.race;
    case 'land': return m.land;
    case 'networth': return m.networth;
    case 'nwpa': return m.land > 0 ? m.networth / m.land : 0;
    case 'rank': return RANK_ORDER[m.title] ?? 0;
  }
}

/**
 * SF-169 Component 5: mobile card-based content for the World Viewer.
 *
 * Roster is rewritten from one-GlanceCard-per-member (Recommended Change #4) to a single
 * bordered list, one compact two-line row per member — a full 25-member kingdom would otherwise
 * be ~13 screens of scroll for a page whose purpose is at-a-glance comparison. A `<select>` above
 * the list drives sorting (dropped entirely in v1.0 — promoted from "nice to have").
 */
export function MobileKingdomViewerShell({
  kingdom,
  isLoading,
  error,
  viewerProvinceId,
  pageTitle,
  emptyMessage,
  refresh,
  browse,
}: KingdomViewerShellProps) {
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const [sortKey, setSortKey] = useState<SortKey>('rank');
  const navigate = useNavigate();

  // Tapping a province jumps straight to Target Finder's detail view (intel-gather + War
  // Room/Thievery/Magic shortcuts) using the snapshot data already in the roster.
  function handleProvinceClick(m: KingdomMember, kd: Kingdom) {
    const target = memberToProvinceSnapshot(m, {
      kingdomId: kd.kingdomId,
      kingdomName: kd.monarchProvinceName || 'Unnamed Kingdom',
      kingdomNw: kd.stats.totalNetworth,
      kingdomLand: kd.stats.totalLand,
    });
    navigate('/world/targets', { state: { presetTarget: target, presetCurrentTick: kd.currentTick } });
  }

  useEffect(() => {
    h1Ref.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentTick = kingdom?.currentTick ?? 0;

  const sortedMembers = useMemo(() => {
    if (!kingdom) return [];
    return [...kingdom.members].sort((a, b) => {
      const av = sortAccessor(a, sortKey);
      const bv = sortAccessor(b, sortKey);
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });
  }, [kingdom, sortKey]);

  return (
    <div className="space-y-3 p-4">
      <h1 ref={h1Ref} tabIndex={-1} className="text-lg font-bold text-game-50 outline-none">
        {pageTitle}
      </h1>

      {browse && (
        <div data-testid="kingdom-browse-bar">
          <KingdomBrowseControls
            onSelect={browse.onSelect}
            onPrevious={browse.onPrevious}
            onNext={browse.onNext}
            onRandom={browse.onRandom}
            previousDisabled={browse.previousDisabled}
            nextDisabled={browse.nextDisabled}
          />
        </div>
      )}

      {isLoading && !kingdom && (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      )}

      {error && (
        <div>
          <ErrorMessage message={error} />
          <div className="mt-4 text-center">
            <button
              onClick={() => refresh()}
              className="px-4 py-2 rounded"
              style={{ backgroundColor: COLORS.btnPrimary, color: COLORS.text.primary, minHeight: 44 }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {!isLoading && !error && !kingdom && (
        <div className="py-16 text-center">
          <p style={{ color: COLORS.text.tertiary }}>{emptyMessage}</p>
          <div className="mt-4">
            <button
              onClick={() => refresh()}
              className="px-4 py-2 rounded"
              style={{ backgroundColor: COLORS.btnPrimary, color: COLORS.text.primary, minHeight: 44 }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {kingdom && (
        <>
          <GlanceCard title={`The Kingdom of ${kingdom.monarchProvinceName || 'Unnamed Kingdom'} (${kingdom.kingdomId})`}>
            <MetricRow label="Provinces" value={`${kingdom.stats.totalProvinces}`} unit="" weight="secondary" />
            <MetricRow label="Total Networth" value={formatNumber(kingdom.stats.totalNetworth)} unit="gc" weight="primary" />
            <MetricRow label="Total Land" value={formatNumber(kingdom.stats.totalLand)} unit="acres" weight="secondary" />
            <MetricRow label="Stance" value={formatEnumLabel(kingdom.stance)} unit="" weight="tertiary" />
            <MetricRow label="Honour" value={kingdom.honourRank ? `#${kingdom.honourRank}` : '-'} unit="" weight="tertiary" />
            <MetricRow label="Wars" value={`⚔ ${kingdom.relationships.atWar}`} unit="" weight="tertiary" />
            <Link
              to={`/world/dossier/${kingdom.kingdomId}`}
              style={{ color: COLORS.link, fontSize: 13, textDecoration: 'underline' }}
            >
              View Dossier
            </Link>
          </GlanceCard>

          <div className="text-sm" style={{ color: COLORS.text.tertiary }}>
            Key:{' '}
            <span style={{ color: COLORS.positive }}>Green / "Protected"</span> = Protection{' '}
            <span style={{ color: COLORS.negative, fontWeight: 700 }}>Red / "♛ Monarch"</span> = Kingdom Monarch{' '}
            <span style={{ fontWeight: 700, color: COLORS.text.primary }}>Bold</span> = You
          </div>

          <label className="flex items-center gap-2 text-sm" style={{ color: COLORS.text.tertiary }}>
            Sort by
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              style={{
                minHeight: 44,
                padding: '4px 8px',
                background: COLORS.inputBg,
                border: `1px solid ${COLORS.inputBorder}`,
                color: COLORS.text.primary,
                fontSize: 13,
              }}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
          </label>

          {kingdom.members.length === 0 ? (
            <div style={{ padding: 32, color: COLORS.text.muted, textAlign: 'center' }}>
              No provinces in this kingdom
            </div>
          ) : (
            <div className="rounded-lg border" style={{ borderColor: COLORS.border }}>
              {sortedMembers.map((m, idx) => {
                const protectedNow = isProtected(m.protectionExpiresAtTick, currentTick);
                const color = m.isMonarch ? COLORS.negative : protectedNow ? COLORS.positive : COLORS.text.primary;
                const isYou = m.provinceId === viewerProvinceId;
                const accessibleName = `${m.provinceName}${m.isMonarch ? ', Monarch' : ''}${!m.isMonarch && protectedNow ? ', Protected' : ''}`;
                return (
                  <button
                    key={m.provinceId}
                    type="button"
                    onClick={() => handleProvinceClick(m, kingdom)}
                    aria-label={accessibleName}
                    className="px-3 py-2"
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      borderTop: idx > 0 ? `1px solid ${COLORS.border}` : undefined,
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span style={{ color, fontWeight: m.isMonarch || isYou ? 700 : 500, fontSize: 14 }}>
                        {m.provinceName}
                        {isYou && <span className="sr-only"> (you)</span>}
                        {m.isMonarch && (
                          <span style={{ color: COLORS.negative, marginLeft: 6, fontSize: 11, fontWeight: 600 }}>♛ Monarch</span>
                        )}
                        {!m.isMonarch && protectedNow && (
                          <span style={{ color: COLORS.positive, marginLeft: 6, fontSize: 11, fontWeight: 600 }}>Protected</span>
                        )}
                      </span>
                      <span
                        style={{
                          color: COLORS.text.primary,
                          fontWeight: 600,
                          fontSize: 13,
                          flexShrink: 0,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatNumber(m.land)} acres
                      </span>
                    </div>
                    <div style={{ color: COLORS.text.tertiary, fontSize: 12, marginTop: 2 }}>
                      <span style={{ color: COLORS.gold, fontVariantNumeric: 'tabular-nums' }}>
                        {formatNumber(m.networth)}gc
                      </span>
                      {' · '}
                      {formatEnumLabel(m.race)} · {formatEnumLabel(m.title)} · {formatNetworthPerAcre(m.networth, m.land)} NW/Acre
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
